import {
    Card,
    Heading,
    PageContainer,
    Text,
    type ListItemData,
    type OptionProps,
    type RadioGroupOption,
} from '@amelio/ui-library';

import { AutomatedReminders } from './AutomatedReminders/AutomatedReminders';
import { CustomizeSurveyEmail } from './CustomizeSurveyEmail/CustomizeSurveyEmail';
import { LaunchReview, type LaunchReviewTranslationKeys } from './LaunchReview/LaunchReview';
import { ResultsVisibility } from './ResultsVisibility/ResultsVisibility';
import { ScheduleCreationHeader } from './ScheduleCreationHeader/ScheduleCreationHeader';
import { SchedulingOptions } from './SchedulingOptions/SchedulingOptions';

import type { ScheduleCreationFormData, ScheduleCreationFormFields } from '../validation';
import type { User } from '@/types/directory/User';
import type { Descendant } from 'slate';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';

const isScheduleCreationFormData = (
    formValues: ScheduleCreationFormData,
): formValues is ScheduleCreationFormData => {
    return 'daysToComplete' in formValues;
};

interface ScheduleCreationProps {
    reviewees: User[];
    title: string;
    subtitle: string;
    options?: RadioGroupOption[];
    frequencyOptions?: OptionProps[];
    managerAccessList: ListItemData[];
    formValues: ScheduleCreationFormData;
    /** The window the reminder rule reads. Never the raw field: an empty field is not an unlimited window. */
    completionWindowDays?: number;
    isLaunching?: boolean;
    scheduleFrequency?: boolean;
    isSendingTestEmail: boolean;
    isSavingSchedule: boolean;
    scheduledAt: Date;
    feedbackFrequency?: string;
    editorKey?: string;
    isLoading?: boolean;
    onSaveSettings: () => void;
    onScheduledAtChange: (date: Date) => void;
    onFeedbackFrequencyChange?: (value: string) => void;
    onSchedulingOptionChange?: (value: string) => void;
    onDaysToCompleteChange?: (value: number) => void;
    onReminderChange: (params: {
        field: keyof typeof ScheduleCreationFormFields;
        value: boolean;
    }) => void;
    testEmailAddress: string;
    onEmailLanguageChange: (value: string) => void;
    onEmailTitleChange: (value: string) => void;
    onEmailMessageChange: (value: Descendant[]) => void;
    onTestEmailAddressChange: (value: string) => void;
    onTestEmail: () => void;
    onLaunchReview: () => void;
    launchTranslationKeys?: LaunchReviewTranslationKeys;
    hasSelfAssessment?: boolean;
    customizeSurveyEmailHeader?: string;
}

const ScheduleCreation = ({
    reviewees,
    title,
    subtitle,
    options,
    frequencyOptions,
    managerAccessList,
    formValues,
    completionWindowDays,
    scheduledAt,
    feedbackFrequency,
    editorKey,
    scheduleFrequency = false,
    isSendingTestEmail,
    isSavingSchedule,
    isLaunching = false,
    isLoading = false,
    onSaveSettings,
    onScheduledAtChange,
    onFeedbackFrequencyChange,
    onSchedulingOptionChange,
    onDaysToCompleteChange,
    onReminderChange,
    testEmailAddress,
    onEmailLanguageChange,
    onEmailTitleChange,
    onEmailMessageChange,
    onTestEmailAddressChange,
    onTestEmail,
    onLaunchReview,
    launchTranslationKeys,
    hasSelfAssessment = false,
    customizeSurveyEmailHeader,
}: ScheduleCreationProps) => {
    const daysToComplete = isScheduleCreationFormData(formValues)
        ? formValues.daysToComplete
        : undefined;

    return (
        <PageContainer className={'flex w-full flex-col gap-8'}>
            {isLoading ? (
                <Skeleton>
                    <Bone className={'h-10 w-40'} />
                </Skeleton>
            ) : (
                <ScheduleCreationHeader
                    reviewees={reviewees}
                    isSavingSchedule={isSavingSchedule}
                    onSaveSettings={onSaveSettings}
                />
            )}

            <Card
                className={'overflow-visible'}
                borderRadius={'2xl'}
            >
                <Heading
                    level={3}
                    weight={'medium'}
                >
                    {title}
                </Heading>

                <Text className={'mb-12'}>{subtitle}</Text>

                {isLoading ? (
                    <Skeleton className={'mt-4 flex flex-col gap-6'}>
                        <Bone className={'h-50 w-full'} />

                        <Bone className={'h-40 w-full'} />

                        <Bone className={'h-85 w-full'} />

                        <Bone className={'h-140 w-full'} />
                    </Skeleton>
                ) : (
                    <>
                        <div className={'relative z-10'}>
                            <SchedulingOptions
                                options={options}
                                frequencyOptions={frequencyOptions}
                                schedulingOption={
                                    isScheduleCreationFormData(formValues)
                                        ? formValues.scheduleType
                                        : undefined
                                }
                                daysToComplete={daysToComplete}
                                scheduleFrequency={scheduleFrequency}
                                scheduledAt={scheduledAt}
                                feedbackFrequency={feedbackFrequency}
                                onSchedulingOptionChange={onSchedulingOptionChange}
                                onDaysToCompleteChange={onDaysToCompleteChange}
                                onScheduledAtChange={onScheduledAtChange}
                                onFeedbackFrequencyChange={onFeedbackFrequencyChange}
                                hasSelfAssessment={hasSelfAssessment}
                            />
                        </div>

                        <AutomatedReminders
                            reminderSevenDays={formValues.reminderSevenDays}
                            reminderThreeDays={formValues.reminderThreeDays}
                            reminderOnDeadline={formValues.reminderOnDeadline}
                            completionWindowDays={completionWindowDays}
                            onReminderChange={onReminderChange}
                        />

                        <ResultsVisibility managerAccessList={managerAccessList} />

                        <CustomizeSurveyEmail
                            emailLanguage={formValues.emailLanguage}
                            emailTitle={formValues.emailTitle}
                            emailMessage={formValues.emailMessage}
                            editorKey={editorKey}
                            testEmailAddress={testEmailAddress}
                            onEmailLanguageChange={onEmailLanguageChange}
                            onEmailTitleChange={onEmailTitleChange}
                            onEmailMessageChange={onEmailMessageChange}
                            onTestEmailAddressChange={onTestEmailAddressChange}
                            onTestEmail={onTestEmail}
                            isSendingTestEmail={isSendingTestEmail}
                            customizeSurveyEmailHeader={customizeSurveyEmailHeader}
                        />
                    </>
                )}
            </Card>

            <LaunchReview
                onLaunchReview={onLaunchReview}
                isLaunching={isLaunching}
                translationKeys={launchTranslationKeys}
            />
        </PageContainer>
    );
};

export { ScheduleCreation };
